/** Segmented tether line connecting two entities — chains, bonds, tethers.
 *
 * Unlike ChannelBeam (a thick sustained beam), ChainTether draws a thin
 * segmented line with lateral jitter between source and target. An optional
 * energy pulse travels along the segments. Canvas2D provides antialiased
 * rendering in pixelArt mode.
 *
 * Usage: call setTarget() to set the far endpoint, then play() to start.
 * Call update() each frame to reposition both endpoints. stop() fades out.
 */
import Phaser from 'phaser';
import { VfxPrimitive, VfxPrimitiveConfig } from './VfxPrimitive';
import { CRISP_SCALE } from '../../ui/CrispUtil';

export interface ChainTetherConfig extends VfxPrimitiveConfig {
  /** Stroke width of the tether line, in logical pixels. */
  lineWidth: number;
  /** Number of segments between source and target. More = more jagged. */
  segmentCount: number;
  /** Maximum lateral jitter per segment, in logical pixels. */
  jitter: number;
  /** If true, segments jitter each frame for a crackling/energy look. */
  animateJitter: boolean;
  /** If true, a bright pulse travels source→target periodically. */
  pulse: boolean;
  /** Period of the travelling pulse in ms. */
  pulsePeriodMs: number;
  /** Width multiplier for the pulse highlight (relative to lineWidth). */
  pulseWidthScale: number;
  /** Duration of form-in animation in ms. */
  formDurationMs: number;
  /** Duration of break/fade animation in ms. */
  breakDurationMs: number;
}

type TetherState = 'inactive' | 'forming' | 'sustained' | 'breaking';

export class ChainTether extends VfxPrimitive<ChainTetherConfig> {
  private canvas!: HTMLCanvasElement;
  private ctx!: CanvasRenderingContext2D;
  private canvasTexture!: Phaser.Textures.CanvasTexture;
  private display!: Phaser.GameObjects.Image;
  private textureKey!: string;

  private state: TetherState = 'inactive';
  private sourceX = 0;
  private sourceY = 0;
  private targetX = 0;
  private targetY = 0;
  private currentAlpha = 0;
  private pulsePhase = 0;
  private formProgress = 0;

  private transitionTween: Phaser.Tweens.Tween | null = null;

  setTarget(tx: number, ty: number): void {
    this.targetX = tx;
    this.targetY = ty;
  }

  protected allocatePool(): void {
    // Canvas large enough for max expected tether length
    const canvasPx = 256 * CRISP_SCALE;
    this.textureKey = `_chain-tether-${Date.now()}`;
    this.canvas = document.createElement('canvas');
    this.canvas.width = canvasPx;
    this.canvas.height = canvasPx;
    this.ctx = this.canvas.getContext('2d')!;

    this.canvasTexture = this.scene.textures.addCanvas(this.textureKey, this.canvas)!;
    this.canvasTexture.setFilter(Phaser.Textures.FilterMode.LINEAR);

    this.display = this.scene.add.image(0, 0, this.textureKey)
      .setOrigin(0.5, 0.5)
      .setBlendMode(this.config.blendMode ?? Phaser.BlendModes.NORMAL);
    this.display.setVisible(false).setActive(false);
    this.pool.push(this.display);
  }

  play(x: number, y: number, depth: number): void {
    if (this.state === 'sustained' || this.state === 'forming') return;
    this.killTransition();
    this.state = 'forming';
    this.sourceX = x;
    this.sourceY = y;
    this.pulsePhase = 0;

    this.display
      .setDepth(depth + (this.config.depthOffset ?? 0))
      .setVisible(true)
      .setActive(true);

    const obj = { alpha: 0, progress: 0 };
    this.transitionTween = this.scene.tweens.add({
      targets: obj,
      alpha: 1,
      progress: 1,
      duration: this.config.formDurationMs,
      ease: 'Quad.easeOut',
      onUpdate: () => {
        this.currentAlpha = obj.alpha;
        this.formProgress = obj.progress;
      },
      onComplete: () => {
        this.state = 'sustained';
        this.currentAlpha = 1;
        this.formProgress = 1;
        this.transitionTween = null;
      },
    });
  }

  stop(): void {
    if (this.state === 'inactive' || this.state === 'breaking') return;
    this.killTransition();
    this.state = 'breaking';

    const obj = { alpha: this.currentAlpha };
    this.transitionTween = this.scene.tweens.add({
      targets: obj,
      alpha: 0,
      duration: this.config.breakDurationMs,
      ease: 'Quad.easeIn',
      onUpdate: () => {
        this.currentAlpha = obj.alpha;
      },
      onComplete: () => {
        this.state = 'inactive';
        this.display.setVisible(false).setActive(false);
        this.transitionTween = null;
      },
    });
  }

  /** Reposition source and target each frame. Also drives jitter animation and pulse. */
  updatePosition(x: number, y: number, depth: number, delta: number): void {
    if (this.state === 'inactive') return;
    this.sourceX = x;
    this.sourceY = y;
    this.display.setDepth(depth + (this.config.depthOffset ?? 0));

    if (this.config.pulse) {
      this.pulsePhase = (this.pulsePhase + delta / this.config.pulsePeriodMs) % 1;
    }

    this.redraw();
  }

  /** Explicit target update + redraw, for callers that track target separately. */
  updateEndpoints(
    sx: number, sy: number,
    tx: number, ty: number,
    depth: number, delta: number,
  ): void {
    this.targetX = tx;
    this.targetY = ty;
    this.updatePosition(sx, sy, depth, delta);
  }

  destroy(): void {
    this.killTransition();
    if (this.textureKey && this.scene.textures.exists(this.textureKey)) {
      this.scene.textures.remove(this.textureKey);
    }
    super.destroy();
  }

  private killTransition(): void {
    if (this.transitionTween) {
      this.transitionTween.destroy();
      this.transitionTween = null;
    }
  }

  /** Redraw the tether line onto the canvas texture. */
  private redraw(): void {
    const { ctx, canvas, config } = this;
    const dx = this.targetX - this.sourceX;
    const dy = this.targetY - this.sourceY;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist < 1) return;

    // Position the display image at the midpoint between source and target
    const midX = (this.sourceX + this.targetX) / 2;
    const midY = (this.sourceY + this.targetY) / 2;
    this.display.setPosition(midX, midY);

    // Size the display to cover the tether
    const margin = config.jitter * 4;
    const displayW = dist + margin * 2;
    const displayH = margin * 2;
    this.display.setDisplaySize(displayW, displayH);

    // Canvas coordinates: line runs left-to-right, centered vertically
    const canvasW = canvas.width;
    const canvasH = canvas.height;
    ctx.clearRect(0, 0, canvasW, canvasH);

    const angle = Math.atan2(dy, dx);
    this.display.setRotation(angle);

    const lw = config.lineWidth * CRISP_SCALE;
    const jit = config.jitter * CRISP_SCALE;
    const segCount = config.segmentCount;
    const effectiveSegs = Math.max(1, Math.ceil(segCount * this.formProgress));

    // Map logical tether to canvas space
    const cxStart = (canvasW - dist * CRISP_SCALE) / 2;
    const cyMid = canvasH / 2;
    const segLen = (dist * CRISP_SCALE) / segCount;

    // Generate segment points with jitter
    const points: { x: number; y: number }[] = [{ x: cxStart, y: cyMid }];
    for (let i = 1; i <= effectiveSegs; i++) {
      const px = cxStart + segLen * i;
      let py = cyMid;
      if (i < effectiveSegs) {
        const lateralJitter = config.animateJitter
          ? (Math.random() - 0.5) * 2 * jit
          : (Math.sin(i * 2.7 + this.pulsePhase * Math.PI * 2) * jit * 0.5);
        py += lateralJitter;
      }
      points.push({ x: px, y: py });
    }

    // Draw the main tether line
    ctx.strokeStyle = `rgba(255, 255, 255, ${this.currentAlpha})`;
    ctx.lineWidth = lw;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) {
      ctx.lineTo(points[i].x, points[i].y);
    }
    ctx.stroke();

    // Draw travelling pulse highlight
    if (config.pulse && this.state === 'sustained') {
      const pulseIdx = this.pulsePhase * (points.length - 1);
      const idx = Math.floor(pulseIdx);
      const frac = pulseIdx - idx;
      if (idx < points.length - 1) {
        const px = points[idx].x + (points[idx + 1].x - points[idx].x) * frac;
        const py = points[idx].y + (points[idx + 1].y - points[idx].y) * frac;
        const pulseR = lw * config.pulseWidthScale;
        ctx.fillStyle = `rgba(255, 255, 255, ${this.currentAlpha * 0.8})`;
        ctx.beginPath();
        ctx.arc(px, py, pulseR, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // Apply tint via the Image (canvas is white, tint colors it)
    this.display.setTint(config.tint);
    this.canvasTexture.refresh();
  }
}
